// Material
import { createContext, useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Local
import createPalette from './palette';
import overrides from './overrides';
import transitions from './transitions';
import typography from './typography';

const COLOR_MODE_STORAGE_KEY = 'ehrms.colorMode.v2';

const getSystemMode = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialPreference = () => {
    if (typeof window === 'undefined') return 'system';
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
};

export const ColorModeContext = createContext({
    mode: 'light',
    preference: 'system',
    toggleColorMode: () => {},
    setColorMode: () => {}
});

export let theme = createTheme({
    palette: createPalette('light'),
    components: overrides,
    spacing: 8,
    transitions,
    typography,
});

export const useColorModeTheme = (mode) => useMemo(() => {
    theme = createTheme({
        palette: createPalette(mode),
        components: overrides,
        spacing: 8,
        transitions,
        typography,
    });
    return theme;
}, [mode]);

export const useThemeModeController = () => {
    const [preference, setPreference] = useState(getInitialPreference); // 'light' | 'dark' | 'system'
    const [systemMode, setSystemMode] = useState(getSystemMode); // actual OS preference

    // Listen for OS preference changes
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // Resolve the actual color mode: if preference is 'system', use the OS preference
    const mode = preference === 'system' ? systemMode : preference;

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, preference);
        document.documentElement.dataset.colorMode = mode;
        document.documentElement.style.colorScheme = mode;
        document.body.dataset.colorMode = mode;
    }, [preference, mode]);

    const setColorMode = (newMode) => {
        if (newMode === 'light' || newMode === 'dark' || newMode === 'system') {
            setPreference(newMode);
        }
    };

    const toggleColorMode = () => setPreference((prev) => (prev === 'light' ? 'dark' : 'light'));

    return { mode, preference, setColorMode, toggleColorMode };
};

export default ({ children }) => {
    const { mode, preference, setColorMode, toggleColorMode } = useThemeModeController();
    const activeTheme = useColorModeTheme(mode);

    return (
        <ColorModeContext.Provider value={{ mode, preference, setColorMode, toggleColorMode }}>
            <ThemeProvider theme={activeTheme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
};
